import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardFooter } from '../../../../../components/Common/UI/Card';
import Icons from '../../../../../components/Common/Icons/Icons';
import { Button } from '../../../../../components/Common/UI/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../../../../components/Common/UI/Form';
import { Input } from '../../../../../components/Common/UI/Input';
import { infoTracUser } from '../../../../../contstant';

const formSchema = z.object({
  username: z.string().refine(value => value === infoTracUser.username, {
    message: 'Incorrect username',
  }),
  password: z
    .string()
    .min(8, {
      message: 'Password must be at least 8 characters.',
    })
    .regex(/^(?=.*[0-9])(?=.*[\D])([\D0-9]+)$/, {
      message: 'Password should include numbers and letters.',
    })
    .regex(/[^A-Za-zА-Яа-яІЇҐЄіїґє0-9]|[A-ZА-ЯІЇҐЄ]/, {
      message: 'The password must contain at least one capital letter or special character',
    })
    .regex(/[a-zа-яіїґє]/, { message: 'Must contain one lowercase letter.' })
    .regex(/^\S+$/g, { message: 'Password must not contain spaces.' }),
});

function SignInInfoTracContent() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleToggleShowPass = () => {
    setShowPassword(prevState => !prevState);
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: infoTracUser.username,
      password: '',
    },
  });

  const usernameWatch = form.watch('username');
  const passwordWatch = form.watch('password');

  function onSubmit(_values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    navigate('/infoTrac/dual-authentication');
  }

  return (
    <div className="text-white">
      <header className="max-w-6xl mb-6 mx-auto px-4 flex flex-col gap-4 items-start text-white">
        <div className="h-20 flex gap-2 items-center text-xl">
          <Icons.InfoImageLogo />
          <div className="flex gap-0.5">
            <span className="italic font-semibold">Info</span>
            <span className="font-extrabold">IMAGE</span>
          </div>
        </div>

        <div>
          <Button variant="ghost" size="auto" onClick={() => navigate('/')}>
            <Icons.ArrowLeft className="rotate-180 child:opacity-100 text-lg" /> Back
          </Button>
        </div>
      </header>

      <div className="flex justify-center items-center m-auto">
        <Card className="bg-[#FFFFFF1A] max-w-md w-full py-12 px-[70px] flex flex-col gap-4">
          <div id="card-header" className="flex gap-3 items-center mb-2">
            <Icons.InfoTracLogo />
            <span className="text-lg tracking-wide">InfoTRAC</span>
          </div>
          <div>
            <h3 className="text-[26px] font-bold">Sign In</h3>
            <p>We’re happy to have you here again!</p>
          </div>

          <CardContent className="p-0 flex flex-col gap-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <FormField
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="Username" variant="home" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Password"
                          type={showPassword ? 'text' : 'password'}
                          variant="home"
                          onFocus={
                            !passwordWatch
                              ? () => form.setValue('password', infoTracUser.passwordPlaceholder)
                              : undefined
                          }
                          icon={
                            <Button
                              type="button"
                              size="auto"
                              variant="ghost"
                              onClick={() => handleToggleShowPass()}
                              className="text-white hover:text-slate-300"
                            >
                              {showPassword ? <Icons.EyeFilled /> : <Icons.EyeFilled />}
                            </Button>
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  variant="default"
                  disabled={!usernameWatch || !passwordWatch}
                  className="disabled:bg-[#8794AC]"
                >
                  Submit
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="p-0 text-center justify-center">
            <Link className="text-[#02B3CB]" to="#">
              Forgot your Password?
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}

export default SignInInfoTracContent;
